/*
 * MockStoreClient — in-repo fixtures so the shell renders with NO backend.
 * ----------------------------------------------------------------------------
 * ALL VALUES HERE ARE PLACEHOLDER / SAMPLE DATA — deliberately generic. This
 * file contains NO real claims about Nick (no live markers, doses, or figures).
 * The real numbers arrive only through EngineStoreClient, packet-grounded. Do
 * not read anything here as a fact about anyone.
 */

import type { StoreClient } from './StoreClient';
import type {
  ActionCard,
  Business,
  ChatMessage,
  Finances,
  Flag,
  Health,
  Identity,
  MacArm,
  School,
  Settings,
  ShoppingItem,
  Stamped,
  Today,
  TodoItem,
  UpdateCard,
  CalendarEvent,
  Freshness,
} from './types';

const NOW = () => new Date().toISOString();

function stamp<T>(data: T, source: string, freshness: Freshness = 'fresh'): Stamped<T> {
  return { data, generated_at: NOW(), source: `MOCK:${source}`, freshness };
}

let chatHistory: ChatMessage[] = [
  {
    id: 'm0',
    role: 'skippy',
    text: 'This is the Skippy shell running on mock data. Wire the engine to make me real.',
    lane: 'FAST',
    receipts: [{ label: 'scaffold', claimClass: 'qualitative' }],
  },
];

let updates: UpdateCard[] = [
  {
    id: 'u1',
    severity: 'needs-nick',
    domain: 'app-tech',
    headline: 'Sample update card (mock)',
    bullets: ['Placeholder bullet one', 'Placeholder bullet two'],
    detail: 'Replace with the real Updates feed via EngineStoreClient.',
    acked: false,
  },
  {
    id: 'u2',
    severity: 'fyi',
    domain: 'family',
    headline: 'Another sample card',
    bullets: ['fyi severity renders silent in the feed'],
    acked: false,
  },
];

let actions: ActionCard[] = [
  {
    id: 'a1',
    kind: 'message',
    channel: 'whatsapp',
    context: 'Inbound message would be quoted here (mock).',
    proposal: 'The proposed reply, drafted in Nick’s voice, would appear here.',
    explain: 'Approving will: send this WhatsApp (mock — nothing is really sent).',
    state: 'pending',
  },
  {
    id: 'a2',
    kind: 'payment',
    channel: 'wise',
    context: 'A payment request (mock).',
    proposal: 'Pay sample vendor.',
    explain: 'Payments ALWAYS require a per-tap approval and can never auto-send.',
    state: 'pending',
  },
];

export class MockStoreClient implements StoreClient {
  async getIdentity(): Promise<Identity> {
    return {
      id: 'nick',
      displayName: 'Nick',
      persona: 'skippy',
      scopes: ['NICK_PRIVATE', 'SHARED'],
    };
  }

  async getToday(): Promise<Stamped<Today>> {
    return stamp(
      {
        greeting: 'Good evening — mock frame',
        tiles: [
          { key: 'up-next', title: 'Up Next', value: '—', sub: 'calendar (mock)', fill: 'sky' },
          { key: 'reps', title: 'Reps Due', value: '0', sub: 'placeholder', fill: 'mint' },
          { key: 'todos', title: 'To-Dos', value: '0', sub: 'placeholder', fill: 'lilac' },
          { key: 'finance', title: 'Finance Pulse', value: '—', sub: 'mock', fill: 'blush' },
          { key: 'hs', title: 'H&S Pulse', value: '—', sub: 'mock', fill: 'butter' },
          { key: 'actions', title: 'Pending Actions', value: '2', sub: 'mock', status: 'watch' },
        ],
      },
      'kv:today',
    );
  }

  async getActions(): Promise<Stamped<ActionCard[]>> {
    return stamp(actions, 'action-queue');
  }

  async getUpdates(): Promise<Stamped<UpdateCard[]>> {
    return stamp(updates, 'kv:nicks-updates');
  }

  async getHealth(): Promise<Stamped<Health>> {
    return stamp(
      {
        panels: [
          {
            person: 'Nick',
            scope: 'NICK_PRIVATE',
            repsDue: ['Sample rep A', 'Sample rep B'],
            markers: [
              {
                name: 'Sample Marker (mock)',
                projectedNow: '— (living estimate placeholder)',
                confidence: 'low',
                status: 'watch',
              },
            ],
          },
        ],
      },
      'kv:health-*',
      'stale',
    );
  }

  async getFinances(): Promise<Stamped<Finances>> {
    return stamp(
      {
        netPulse: '— (mock)',
        budgetLines: [
          { name: 'Sample category', target: 1000, actual: 640 },
          { name: 'Another category', target: 500, actual: 512 },
        ],
      },
      'kv:finances',
    );
  }

  async getBusiness(): Promise<Stamped<Business>> {
    return stamp(
      {
        kpis: [
          { key: 'mrr', title: 'Revenue (mock)', value: '—', fill: 'mint' },
          { key: 'margin', title: 'Margin (mock)', value: '—', fill: 'sky' },
        ],
        openLoops: ['Sample open loop (mock)'],
      },
      'kv:hs-finance',
    );
  }

  async getCalendar(): Promise<Stamped<CalendarEvent[]>> {
    return stamp(
      [
        { id: 'e1', when: 'Today', title: 'Sample event (mock)' },
        { id: 'e2', when: 'Tomorrow', title: 'Another event (mock)' },
      ],
      'kv:ical-raw-cache',
    );
  }

  async getTodos(): Promise<Stamped<TodoItem[]>> {
    return stamp(
      [
        { id: 't1', title: 'Sample to-do (mock)', done: false },
        { id: 't2', title: 'Done to-do (mock)', done: true },
      ],
      'monday:2689216450',
    );
  }

  async getShopping(): Promise<Stamped<ShoppingItem[]>> {
    return stamp(
      [
        { id: 's1', title: 'Sample item (mock)', got: false },
        { id: 's2', title: 'Got item (mock)', got: true },
      ],
      'monday:18420185357',
    );
  }

  async getExtras(): Promise<Stamped<MacArm>> {
    // Models the fail-soft state: when the Mac arm is offline, surfaces say so honestly.
    return stamp(
      {
        online: false,
        services: [
          { name: 'Vault', ok: false },
          { name: 'Transcribe', ok: false },
          { name: 'WhatsApp session', ok: false },
        ],
      },
      'mac-arm',
      'offline',
    );
  }

  async getSchool(): Promise<Stamped<School>> {
    return stamp(
      {
        kids: [
          { name: 'Kid 1', lastResult: '— (mock)' },
          { name: 'Kid 2', lastResult: '— (mock)' },
        ],
      },
      'skippy-school',
    );
  }

  async getCritic(): Promise<Stamped<Flag[]>> {
    // PLACEHOLDER flags so the offline shell renders the surface — NOT real critic output.
    const flags: Flag[] = [
      {
        title: 'Possible: sample critic flag (mock — not a real finding)',
        question: 'This is a placeholder question the real critic would ask — wire the engine to see genuine flags?',
        score_1_to_10: 3,
        confidence: 'low',
        evidence: [{ source_id: 'mock:placeholder', text: 'Placeholder evidence citation.' }],
        systems_touched: ['S12'],
      },
      {
        title: 'Possible: second sample flag (mock)',
        question: 'Another placeholder — does the offline shell render multiple flag cards?',
        score_1_to_10: 5,
        confidence: 'med',
        evidence: [
          { source_id: 'mock:placeholder-a', text: 'First placeholder evidence line.' },
          { source_id: 'mock:placeholder-b', text: 'Second placeholder evidence line.' },
        ],
        systems_touched: ['S12', 'S1'],
      },
    ];
    return stamp(flags, 'engine:critic-flags.json');
  }

  async getSettings(): Promise<Stamped<Settings>> {
    return stamp(
      {
        trustLadder: [
          { label: 'WhatsApp → sample contact', autoSend: false, approvedUnchanged: 3 },
          { label: 'Payments (any)', autoSend: false, approvedUnchanged: 0 },
        ],
        spendTodayUSD: 0,
        spendCapUSD: 3,
        macArmOnline: false,
        brainGeneratedAt: NOW(),
      },
      'settings',
    );
  }

  async getChatHistory(): Promise<ChatMessage[]> {
    return chatHistory;
  }

  async sendChat(text: string, lane: 'DEEP' | 'FAST'): Promise<ChatMessage> {
    const user: ChatMessage = { id: `u${Date.now()}`, role: 'user', text, lane };
    const reply: ChatMessage = {
      id: `s${Date.now()}`,
      role: 'skippy',
      lane,
      text: `[mock ${lane}] The engine is not wired yet, so I can only echo: “${text}”`,
      receipts: [{ label: 'mock — no packet', claimClass: 'qualitative' }],
    };
    chatHistory = [...chatHistory, user, reply];
    return reply;
  }

  async decideAction(id: string, decision: 'approve' | 'edit' | 'skip', editedText?: string): Promise<ActionCard> {
    const card = actions.find((a) => a.id === id);
    if (!card) throw new Error(`no action ${id}`);
    if (decision === 'skip') card.state = 'skipped';
    else {
      card.state = 'sent';
      card.receipt = `mock ${card.kind} “sent” ${new Date().toLocaleTimeString()}`;
      if (editedText) card.proposal = editedText;
    }
    actions = actions.map((a) => (a.id === id ? card : a));
    return card;
  }

  async ackUpdate(id: string): Promise<void> {
    updates = updates.map((u) => (u.id === id ? { ...u, acked: true } : u));
  }

  // No native push in the browser/mock build — accept and drop the token.
  async registerPush(_token: string, _platform: string): Promise<void> {
    /* no-op */
  }
}
